import { CommandBarButton, IContextualMenuProps } from "@fluentui/react";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import clsx from "clsx";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";
import { accent } from "../../config/colors";
import { useSelector } from "../../store/hooks";
import { transformReportsForExcel } from "../../utils/excel";
import { createPDFReports } from "../../utils/pdf";
import { Report } from "../../types/types";
import { TableSpinner } from "../../components/TableSpinner";

type Props = {
  refExcelExport: ExcelExport;
  filteredReports: Report[];
  visibleLabelKeys?: string[];
};

export const ExportMenu = ({
  refExcelExport,
  filteredReports,
  visibleLabelKeys,
}: Props) => {
  const { t } = useTranslation();
  const taxonomy = useSelector((s) => s.taxonomy);
  const [isPDFLoading, setIsPDFLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const isAnyLoading = isPDFLoading || isExcelLoading;

  const handleExportPDF = () => {
    setIsPDFLoading(true);
    try {
      createPDFReports({
        reports: filteredReports,
        taxonomy,
        t,
        visibleLabelKeys,
      }).open();
    } finally {
      setIsPDFLoading(false);
    }
  };

  const handleExportExcel = () => {
    setIsExcelLoading(true);
    try {
      const data = transformReportsForExcel({
        reports: filteredReports,
        t,
        taxonomy,
      });
      refExcelExport?.save(data);
    } finally {
      setIsExcelLoading(false);
    }
  };

  const menuProps: IContextualMenuProps = {
    items: [
      {
        key: "exportPDF",
        text: t("greco.exportPDF"),
        iconProps: {
          iconName: isPDFLoading ? "ProgressRingDots" : "PDF",
          style: { color: accent.red },
        },
        disabled: isPDFLoading,
        onClick: handleExportPDF,
      },
      {
        key: "exportExcel",
        text: "Export Excel",
        iconProps: {
          iconName: isExcelLoading ? "ProgressRingDots" : "ExcelDocument",
          style: { color: "green" },
        },
        disabled: isExcelLoading,
        onClick: handleExportExcel,
      },
    ],
  };

  return (
    <>
      <StyledCommandBarButton
        text="Export"
        iconProps={{ iconName: "Export" }}
        menuProps={menuProps}
        disabled={isAnyLoading}
      />
      {isAnyLoading && <TableSpinner />}
    </>
  );
};

const spin = keyframes`
  from {
    transform: rotateZ(0deg)
  }
  to {
    transform: rotateZ(360deg)
  }
`;

const StyledCommandBarButton = styled(CommandBarButton)`
  height: 32px;
  &.isLoading {
    i {
      animation: ${spin} 500ms linear infinite;
    }
  }
`;
